import { SavedReport } from "../types";

interface SaveReportRequest {
  reportId?: string | null;
  producerId: string;
  producerName: string | null;
  producerEmail: string | null;
  orgnr: string | null;
  year: number;
  comment: string | null;
  reportedBy: string;
  numbers: {
    numberId?: string | null;
    reportId?: string | null;
    productName: string;
    volume: number;
  }[];
}

/* Helpers */
type ApiResult<T> = {
  success: boolean;
  error: string | null;
  data: T;
};

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `API error ${res.status} ${res.statusText}${text ? ` - ${text}` : ""}`
    );
  }
  return (await res.json()) as T;
}

async function handleWrappedResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `API error ${res.status} ${res.statusText}${text ? ` - ${text}` : ""}`
    );
  }
  const json = (await res.json()) as ApiResult<T>;
  if (!json.success) {
    throw new Error(json.error ?? "Ukjent API-feil");
  }
  return json.data;
}

/* Actor dashboard API functions */

// Get reports for producer by producer id
export async function getReportsByProducer(producerId: string): Promise<SavedReport[]> {
  const res = await fetch(
    `/api/report/getreportsbyproducer?producerId=${encodeURIComponent(producerId)}`,
    {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    }
  );

  return handleWrappedResponse<SavedReport[]>(res);
}

async function getProducerReportByYear(
  producerId: string,
  year: number
): Promise<SavedReport | null> {
  const all = await getReportsByProducer(producerId);

  const reportsForYear = all.filter((r) => r.year === year);
  if (reportsForYear.length === 0) return null;

  const chosen =
    reportsForYear.find((r) => !!r.reportDate) ?? reportsForYear[0];

  const full = await getReportById(chosen.reportId);
  return full;
}

export async function getProducerReportsByYears(
  producerId: string,
  years: number[]
): Promise<Record<number, SavedReport>> {
  const result: Record<number, SavedReport> = {};

  for (const year of years) {
    const report = await getProducerReportByYear(producerId, year);
    if (report) {
      result[year] = report;
    }
  }

  return result;
}

// Get report by report id
export async function getReportById(reportId: string): Promise<SavedReport> {
  const res = await fetch(
    `/api/report/getreportbyid?reportId=${encodeURIComponent(reportId)}`,
    { method: "GET", credentials: "include", headers: { Accept: "application/json" } }
  );
  return handleResponse<SavedReport>(res);
}

// Save new report or update existing one
export async function saveReport(
  payload: SaveReportRequest
): Promise<SavedReport> {
  const res = await fetch("/api/report/savereport", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  return handleWrappedResponse<SavedReport>(res);
}

// Confirm report
export async function confirmReport(params: {
  reportId: string;
  reporterName: string;
  phoneNumber: string;
}): Promise<boolean> {
  const res = await fetch("/api/report/confirmreport", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(params),
  });

  return handleWrappedResponse<boolean>(res);
}
